import { useEffect, useRef } from "react";
import { useLang } from "../context/LanguageContext";
import { CHAPTERS } from "../data/content";

export default function Story() {
  const { t } = useLang();
  const sectionRef = useRef(null);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll(".reveal") || [];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.18 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="story"
      ref={sectionRef}
      className="relative py-32 lg:py-48 paper-grain"
      data-testid="story-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="reveal mb-24 text-center">
          <p className="chapter-num mb-4">{t("the story", "הסיפור")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-parchment leading-tight">
            {t("I wasn't born confident.", "לא נולדתי עם ביטחון עצמי.")}
            <br />
            <em className="text-four-yellow">
              {t("I had to build it.", "הייתי צריך לבנות את זה.")}
            </em>
          </h2>
          <div className="brush-divider mx-auto" />
        </div>

        <div className="space-y-32 lg:space-y-48">
          {CHAPTERS.map((ch, idx) => (
            <article
              key={ch.id}
              className="reveal grid lg:grid-cols-12 gap-10 lg:gap-16 items-center"
              data-testid={`chapter-${ch.id}`}
            >
              <div className={`lg:col-span-5 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div
                  className="ink-circle relative mx-auto"
                  style={{ width: "min(360px, 75vw)", height: "min(360px, 75vw)" }}
                >
                  <img
                    src={ch.image}
                    alt={t(ch.title.en, ch.title.he)}
                    className="absolute inset-4 w-[calc(100%-2rem)] h-[calc(100%-2rem)] object-cover rounded-full opacity-90"
                  />
                </div>
              </div>

              <div className={`lg:col-span-7 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                <p className="chapter-num mb-4">
                  {t(`Chapter ${ch.num}`, `פרק ${ch.num}`)}
                </p>
                <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-parchment leading-[1.1] mb-6">
                  {t(ch.title.en, ch.title.he)}
                </h3>
                <div className="brush-divider" />
                <p
                  className="text-base sm:text-lg leading-relaxed text-parchment-warm/90 font-light drop-cap"
                  style={{ fontWeight: 300 }}
                >
                  {t(ch.body.en, ch.body.he)}
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
